import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import './index.scss';

export interface IExpandableSummaryProps {
  html: string;
  className?: string;
  maxLines?: number;
}

const LINE_HEIGHT_PX = 22;

/**
 * Studio 报告摘要 3 行截断 + More/Less（独立实现）
 */
const ExpandableSummary: React.FC<IExpandableSummaryProps> = ({
  html,
  className,
  maxLines = 3,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();
  const [isShow, setIsShow] = useState<boolean>(false);
  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const [summaryLineHeight, setSummaryLineHeight] = useState<string>(
    `${LINE_HEIGHT_PX}px`,
  );
  const [clampMaxHeight, setClampMaxHeight] = useState<string>(
    `${LINE_HEIGHT_PX * maxLines}px`,
  );

  const measureText = useCallback((): void => {
    const el = textRef.current;
    if (!el) return;

    const lineHeight =
      Number.parseFloat(getComputedStyle(el).lineHeight) || LINE_HEIGHT_PX;
    const clampHeight = lineHeight * maxLines;

    setSummaryLineHeight(`${lineHeight}px`);
    setClampMaxHeight(`${clampHeight}px`);

    if (isShow) {
      setIsOverflow(true);
      return;
    }
    // 考虑列表的时候，有margin为2，三行则为6，再+2兜底 
    const overflow = el.scrollHeight > clampHeight + maxLines * 2 + 2;
    setIsOverflow(overflow);
  }, [isShow, maxLines]);

  // ResizeObserver 覆盖所有尺寸变化场景（窗口 resize、内容变化、展开/收起），
  // 无需额外监听 window.resize 事件
  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    // 首次 mount 立即测量一次
    measureText();

    const observer = new ResizeObserver(() => {
      measureText();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [measureText]);

  const getBtnLabel = (): string => {
    return formatMessage({
      id: isShow ? 'dbi_less' : 'dbi_more',
    });
  };

  const handleToggle = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsShow(!isShow);
  };

  const showBtn = isOverflow || isShow;

  return (
    <div className={classNames('dbi-expandable-summary-host', className)}>
      <div
        className={classNames('dbi-expandable-summary-wrapper', {
          show: isShow,
          hide: !isShow,
        })}
        style={{
          ['--summary-max-lines' as string]: maxLines,
          ['--summary-line-height' as string]: summaryLineHeight,
          ['--summary-clamp-max-height' as string]: clampMaxHeight,
        }}
      >
        <div ref={textRef} className="dbi-expandable-summary__text">
          <span
            className="dbi-expandable-summary__context"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
        {showBtn && !isShow && (
          <button
            type="button"
            className="dbi-expandable-summary__text-btn dbi-expandable-summary__text-btn--collapse"
            onClick={handleToggle}
          >
            <span className="dbi-expandable-summary__ellipsis">...</span>
            {getBtnLabel()}
          </button>
        )}
        {showBtn && isShow && (
          <button
            type="button"
            className="dbi-expandable-summary__text-btn dbi-expandable-summary__text-btn--expand"
            onClick={handleToggle}
          >
            {getBtnLabel()}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpandableSummary;
